import { isSelectOption } from "../../../../libs/utils";
import { toRelativeDocumentUrl } from "../../../../organism/ImagesAndQrCode/utils";
import {
  buildMarketingQrCodeUrl,
  normalizeMarketingUrl,
  stripMarketingUrlSchemeForDisplay,
} from "./marketingUrlUtils";

type SelectLike = { label: string; value: string | number };
type GenericObject = Record<string, unknown>;

const toLabelValue = (option: SelectLike) => ({
  label: String(option.label ?? ""),
  value: option.value,
});

const normalizeToOptionsArray = (value: unknown): SelectLike[] => {
  if (Array.isArray(value)) {
    return value.filter(isSelectOption).map((option) => ({
      label: String(option.label ?? ""),
      value: option.value,
    }));
  }

  if (isSelectOption(value)) {
    return [{ label: String(value.label ?? ""), value: value.value }];
  }

  return [];
};

const stripSlugFromQrUrl = (qrCodeUrl: string, slug: string) => {
  const base = String(qrCodeUrl || "").trim().replace(/\/+$/, "");
  const path = String(slug || "").trim().replace(/^\/+/, "");
  if (!base || !path) return base;

  const suffix = `/${path}`;
  if (base.toLowerCase().endsWith(suffix.toLowerCase())) {
    return base.slice(0, -suffix.length).replace(/\/+$/, "");
  }

  return base;
};

/** Fields moved out of Marketing into Property/Unit Information — never send in marketingData. */
const EXCLUDED_MARKETING_KEYS = new Set([
  "pointsOfInterestDistances",
  "numberOfFloors",
  "parkingTypes",
  "parkingPlaces",
  "parkingSpaces",
  "furnished",
]);

const MEDIA_FILE_KEYS = new Set(["floorPlanImage", "unitVideo"]);

type BuildMarketingDataPayloadOptions = {
  mediaPathPrefix?: string;
};

const serializeMarketingMedia = (value: unknown, pathPrefix?: string) => {
  if (value == null) return null;
  if (typeof value !== "object") return null;
  const doc = value as { documentId?: string; documentUrl?: string };
  if (!doc.documentId) return null;
  return {
    documentId: doc.documentId,
    documentUrl: toRelativeDocumentUrl(doc.documentUrl ?? "", pathPrefix),
  };
};

/** Build PATCH body `{ marketingData: { ... } }` from marketing form values. */
export const buildMarketingDataPayload = (
  dynamicFields: GenericObject = {},
  options: BuildMarketingDataPayloadOptions = {},
): { marketingData: GenericObject } => {
  const { mediaPathPrefix } = options;
  const distances = (dynamicFields.pointsOfInterestDistances || {}) as GenericObject;
  const marketingData: GenericObject = {};
  const slug = String(dynamicFields.slug || "").trim();
  const qrCodeUrl = String(dynamicFields.qrCodeUrl || "").trim();

  Object.entries(dynamicFields).forEach(([key, value]) => {
    if (EXCLUDED_MARKETING_KEYS.has(key)) return;

    if (key === "slug") {
      marketingData.slug = slug;
      return;
    }

    if (key === "qrCodeUrl") {
      // Persist full listing URL: marketingPublicListUrl (form value) + slug
      marketingData.qrCodeUrl = buildMarketingQrCodeUrl(normalizeMarketingUrl(qrCodeUrl), slug);
      return;
    }

    if (key === "virtualTourUrl") {
      marketingData.virtualTourUrl = normalizeMarketingUrl(value);
      return;
    }

    if (key === "pointsOfInterest") {
      marketingData.pointsOfInterest = normalizeToOptionsArray(value).map((option) => ({
        ...toLabelValue(option),
        distance: Number(distances[String(option.value)] ?? 0),
      }));
      return;
    }

    if (key === "amenities") {
      marketingData.amenities = normalizeToOptionsArray(value).map(toLabelValue);
      return;
    }

    if (MEDIA_FILE_KEYS.has(key)) {
      if (Object.prototype.hasOwnProperty.call(dynamicFields, key)) {
        marketingData[key] = serializeMarketingMedia(value, mediaPathPrefix);
      }
      return;
    }

    if (isSelectOption(value)) {
      marketingData[key] = toLabelValue(value);
      return;
    }

    if (Array.isArray(value) && value.every(isSelectOption)) {
      marketingData[key] = value.map((option) => toLabelValue(option));
      return;
    }

    marketingData[key] = value;
  });

  return { marketingData };
};

/**
 * Flatten API `marketingData` into form `dynamicFields` shape
 * (POI distances split out).
 */
export const flattenMarketingDataForForm = (marketingData?: GenericObject | null): GenericObject => {
  if (!marketingData || typeof marketingData !== "object") return {};

  const result: GenericObject = { ...marketingData };
  EXCLUDED_MARKETING_KEYS.forEach((key) => {
    if (key !== "pointsOfInterestDistances") delete result[key];
  });

  const slug = String(result.slug || "").trim();

  if (typeof result.qrCodeUrl === "string") {
    // Form keeps base URL; full URL (base + slug) is rebuilt on save
    result.qrCodeUrl = stripMarketingUrlSchemeForDisplay(
      stripSlugFromQrUrl(String(result.qrCodeUrl), slug),
    );
  }

  if (typeof result.virtualTourUrl === "string") {
    result.virtualTourUrl = stripMarketingUrlSchemeForDisplay(String(result.virtualTourUrl));
  }

  // Legacy marketing media keys
  if (result.heroImage && !result.floorPlanImage) {
    result.floorPlanImage = result.heroImage;
  }
  if (result.marketingVideo && !result.unitVideo) {
    result.unitVideo = result.marketingVideo;
  }
  delete result.heroImage;
  delete result.marketingVideo;

  MEDIA_FILE_KEYS.forEach((key) => {
    const value = result[key];
    if (typeof value === "string" && value.trim()) {
      result[key] = { documentId: value, documentUrl: "" };
      return;
    }
    if (value && typeof value === "object") {
      const doc = value as { documentId?: unknown; documentUrl?: unknown };
      if (doc.documentId) {
        result[key] = {
          documentId: String(doc.documentId),
          documentUrl: doc.documentUrl != null ? String(doc.documentUrl) : "",
        };
      }
    }
  });

  if (Array.isArray(result.pointsOfInterest)) {
    const distances: GenericObject = {};
    result.pointsOfInterest = result.pointsOfInterest.map((item) => {
      if (!item || typeof item !== "object") return item;

      const { distance, label, value, ...rest } = item as GenericObject & {
        distance?: unknown;
        label?: unknown;
        value?: unknown;
      };

      if (value != null && distance != null && distance !== "") {
        distances[String(value)] = distance;
      }

      return {
        ...rest,
        label: label != null ? String(label) : "",
        value,
      };
    });
    result.pointsOfInterestDistances = distances;
  }

  return result;
};
